import moment from 'moment'
import { rem } from 'polished'
import React, {
  MouseEventHandler,
  useCallback,
  useEffect,
  useState,
} from 'react'
import styled from 'styled-components'

import Breakpoint from 'components/Common/Breakpoint'
import DateRangePicker from 'components/Common/Calendar/DateRangePicker'
import DualDateRangePicker from 'components/Common/Calendar/DualDateRangePicker'
import TextButton from 'components/Luxkit/Button/TextButton'
import Dropdown from 'components/Luxkit/Dropdown/Dropdown'
import LineArrowRightIcon from 'components/Luxkit/Icons/line/LineArrowRightIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import {
  SHORT_DAY_NAME_DAY_MONTH,
  SHORT_DAY_NAME_DAY_MONTH_YEAR,
} from 'constants/dateFormats'
import { pluralizeToString } from 'lib/string/pluralize'
import { stopPropagation } from 'lib/web/eventHandlerUtils'

function getRangeDescription(
  startDate: moment.Moment,
  endDate: moment.Moment,
  rangeType: 'days' | 'nights',
) {
  if (rangeType === 'days') {
    return pluralizeToString('day', endDate.diff(startDate, 'days') + 1)
  } else {
    return pluralizeToString('night', endDate.diff(startDate, 'days'))
  }
}

const Container = styled.div`
  cursor: default;
  max-width: calc(100vw - ${rem(32)});
  box-shadow: ${(props) => props.theme.shadow.bottom.small};
  border: 1px solid ${(props) => props.theme.palette.neutral.default.six};
  border-radius: ${(props) => props.theme.borderRadius.S};
  background-color: ${(props) => props.theme.palette.neutral.default.eight};
  min-width: ${rem(540)};
  padding: ${rem(20)};
  overflow: auto;
  -webkit-overflow-scrolling: touch;

  ${mediaQueryUp.desktop} {
    min-width: ${rem(1000)};
  }
`

interface Props {
  triggerRef: React.RefObject<HTMLElement>
  isOpen: boolean
  startDate?: moment.Moment
  endDate?: moment.Moment
  onDatesChange: (dates: {
    startDate: moment.Moment
    endDate?: moment.Moment | null
  }) => void
  onClear?: () => void
  onClose?: MouseEventHandler
  startLabel?: string
  endLabel?: string
  rangeType: 'days' | 'nights'
  initDate?: moment.Moment
}

export default function DateRangeDropdown({
  triggerRef,
  isOpen,
  startDate: startDateProp,
  endDate: endDateProp,
  onDatesChange,
  onClear,
  onClose,
  startLabel,
  endLabel,
  rangeType,
  initDate,
}: Props) {
  const [startDate, setStartDate] = useState<moment.Moment | undefined>(
    startDateProp,
  )
  const [endDate, setEndDate] = useState<moment.Moment | undefined>(endDateProp)

  const hasDates = !!(startDate && endDate)

  useEffect(() => {
    setStartDate(startDateProp)
    setEndDate(endDateProp)
  }, [startDateProp, endDateProp])

  const updateDates = useCallback(
    ({
      startDate,
      endDate,
    }: {
      startDate: moment.Moment
      endDate?: moment.Moment | null
    }) => {
      setStartDate(startDate)
      setEndDate(endDate ?? undefined)
    },
    [],
  )

  const apply = useCallback(() => {
    if (startDate) {
      onDatesChange({ startDate, endDate })
    }
  }, [startDate, endDate, onDatesChange])

  return (
    <Dropdown
      triggerRef={triggerRef}
      open={isOpen}
      position="bottom"
      onClose={onClose}
      crossAxisShift
    >
      <Container onClick={stopPropagation}>
        <Breakpoint max="tablet">
          <DateRangePicker
            onDatesChange={updateDates}
            startDate={startDate}
            endDate={endDate}
            startLabel={startLabel}
            endLabel={endLabel}
            initDate={initDate}
          />
        </Breakpoint>
        <Breakpoint min="desktop">
          <DualDateRangePicker
            onDatesChange={updateDates}
            startDate={startDate}
            endDate={endDate}
            startLabel={startLabel}
            endLabel={endLabel}
            initDate={initDate}
          />
        </Breakpoint>
        {!!onClear && (
          <Group direction="horizontal" horizontalAlign="end" gap={16}>
            {hasDates && (
              <Group direction="horizontal" gap={8} verticalAlign="center">
                <Group direction="horizontal" gap={4} verticalAlign="center">
                  <BodyText variant="medium" weight="bold">
                    {startDate.format(SHORT_DAY_NAME_DAY_MONTH)}
                  </BodyText>

                  <LineArrowRightIcon size="S" />

                  <BodyText variant="medium" weight="bold">
                    {endDate.format(SHORT_DAY_NAME_DAY_MONTH_YEAR)}
                  </BodyText>
                </Group>

                <BodyText variant="medium">
                  ({getRangeDescription(startDate, endDate, rangeType)})
                </BodyText>
              </Group>
            )}
            <TextButton kind="tertiary" onClick={onClear} outdent="right">
              Clear dates
            </TextButton>
            <TextButton kind="primary" onClick={apply} disabled={!startDate}>
              Apply
            </TextButton>
          </Group>
        )}
      </Container>
    </Dropdown>
  )
}
